import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSearch } from "@tanstack/react-router";
import { PollyClient, DescribeVoicesCommand, SynthesizeSpeechCommand, type VoiceId } from "@aws-sdk/client-polly";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";
import "../css/Courses.css";
import { apiService, type CourseDetailResponse, type CourseWithProgressResponse, type LabProgressResponse, type TtsVoice } from "../services/api";
import ProtectedRoute from "../components/ProtectedRoute";
import Footer from "../components/Footer";
import { useAuth } from "../hooks/useAuth";
import { useUserSettings } from "../hooks/useUserSettings";
import LabModal from "../components/LabModal";
import StickyVideoPlayer from "../components/StickyVideoPlayer";

export const Route = createFileRoute("/courses/$id")({
  component: CourseDetail,
});

type StoryBeatSection = {
  id: string;
  title?: string;
  html: string;
};

const HEADING_TAGS = new Set(["H2", "H3", "H4"]);

const buildStoryBeatSections = (html?: string | null): StoryBeatSection[] => {
  if (!html) return [];
  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return [{ id: "beat-1", html }];
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const nodes = Array.from(doc.body.childNodes);
  const sections: StoryBeatSection[] = [];

  let currentTitle: string | undefined;
  let currentNodes: Node[] = [];

  const pushSection = () => {
    if (currentNodes.length === 0) return;
    const wrapper = document.createElement("div");
    currentNodes.forEach((node) => wrapper.appendChild(node.cloneNode(true)));
    const sectionHtml = wrapper.innerHTML.trim();
    if (sectionHtml) {
      sections.push({
        id: `beat-${sections.length + 1}`,
        title: currentTitle,
        html: sectionHtml
      });
    }
    currentTitle = undefined;
    currentNodes = [];
  };

  nodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      if (HEADING_TAGS.has(element.tagName)) {
        pushSection();
        currentTitle = element.textContent?.trim() || undefined;
        currentNodes.push(node);
        return;
      }
    }

    currentNodes.push(node);
  });

  pushSection();

  if (sections.length === 0) {
    return [{ id: "beat-1", html }];
  }

  return sections;
};

const extractPlainText = (html: string) => {
  if (typeof window === "undefined" || typeof DOMParser === "undefined") return html;
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  
  // Remove code blocks and diagrams (pre/code tags) before extracting text for TTS
  const codeBlocks = doc.querySelectorAll("pre, code");
  codeBlocks.forEach((block) => block.remove());
  
  return doc.body.textContent?.replace(/\s+/g, " ").trim() || "";
};

const POLLY_MAX_CHARS = 2800; // Safety buffer below 3000 char limit

const splitTextIntoChunks = (text: string, maxChars: number = POLLY_MAX_CHARS): string[] => {
  if (text.length <= maxChars) return [text];
  
  const chunks: string[] = [];
  const paragraphs = text.split(/\n\n+/);
  let currentChunk = '';
  
  for (const paragraph of paragraphs) {
    const trimmedParagraph = paragraph.trim();
    if (!trimmedParagraph) continue;
    
    // If single paragraph exceeds limit, split by sentences
    if (trimmedParagraph.length > maxChars) {
      if (currentChunk) {
        chunks.push(currentChunk.trim());
        currentChunk = '';
      }
      
      const sentences = trimmedParagraph.match(/[^.!?]+[.!?]+/g) || [trimmedParagraph];
      for (const sentence of sentences) {
        if (currentChunk.length + sentence.length + 1 > maxChars) {
          if (currentChunk) {
            chunks.push(currentChunk.trim());
          }
          currentChunk = sentence;
        } else {
          currentChunk += (currentChunk ? ' ' : '') + sentence;
        }
      }
      continue;
    }
    
    // Check if adding this paragraph would exceed limit
    const potentialLength = currentChunk.length + (currentChunk ? 2 : 0) + trimmedParagraph.length;
    
    if (potentialLength > maxChars) {
      if (currentChunk) {
        chunks.push(currentChunk.trim());
      }
      currentChunk = trimmedParagraph;
    } else {
      currentChunk += (currentChunk ? '\n\n' : '') + trimmedParagraph;
    }
  }
  
  if (currentChunk) {
    chunks.push(currentChunk.trim());
  }
  
  return chunks.length > 0 ? chunks : [text];
};

const isReadableStream = (stream: unknown): stream is ReadableStream<Uint8Array> => {
  if (typeof ReadableStream === "undefined") return false;
  return !!stream && typeof (stream as ReadableStream<Uint8Array>).getReader === "function";
};

const buildAudioBlob = async (audioStream: unknown): Promise<Blob> => {
  if (audioStream instanceof Blob) {
    return audioStream;
  }
  if (audioStream instanceof ArrayBuffer) {
    return new Blob([audioStream], { type: "audio/mpeg" });
  }
  if (ArrayBuffer.isView(audioStream)) {
    return new Blob([audioStream.buffer], { type: "audio/mpeg" });
  }
  if (isReadableStream(audioStream)) {
    return new Response(audioStream).blob();
  }
  throw new Error("Unsupported audio stream type");
};

function CourseDetail() {
  const { id } = Route.useParams();
  const search = useSearch({ from: "/courses/$id" });
  const navigate = useNavigate();
  const [courseData, setCourseData] = useState<CourseDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [activeBeatIndex, setActiveBeatIndex] = useState(0);
  const [showAllBeats, setShowAllBeats] = useState(false);
  const beatSectionRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [activeLabId, setActiveLabId] = useState<string | null>(null);
  const [labProgress, setLabProgress] = useState<Record<string, LabProgressResponse>>({});
  const [courseProgress, setCourseProgress] = useState<CourseWithProgressResponse | null>(null);
  const { user } = useAuth();
  const { settings } = useUserSettings();
  const pollyRegion = import.meta.env.VITE_AWS_REGION || "us-east-1";
  const cognitoIdentityPoolId = import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID;
  const pollyClient = useMemo(() => {
    if (!cognitoIdentityPoolId || typeof window === "undefined") return null;
    return new PollyClient({
      region: pollyRegion,
      credentials: fromCognitoIdentityPool({
        clientConfig: { region: pollyRegion },
        identityPoolId: cognitoIdentityPoolId,
      }),
    });
  }, [cognitoIdentityPoolId, pollyRegion]);
  const [isNarrating, setIsNarrating] = useState(false);
  const [ttsVoices, setTtsVoices] = useState<TtsVoice[]>([]);
  const [voiceGender, setVoiceGender] = useState<'all' | 'female' | 'male'>('all');
  const [selectedVoiceId, setSelectedVoiceId] = useState('');
  const [ttsError, setTtsError] = useState<string | null>(null);
  const [isVoicesLoading, setIsVoicesLoading] = useState(false);
  const [isNarrationLoading, setIsNarrationLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const ttsRequestRef = useRef(0);
  const streamingReaderRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const mediaSourceRef = useRef<MediaSource | null>(null);
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0);
  const [totalChunks, setTotalChunks] = useState(1);

  const course = courseData?.course;
  const courseContent = course?.content;
  const pages = courseContent?.pages || [];
  const currentPage = courseContent?.pages?.[currentPageIndex];
  const storyBeatSections = useMemo(
    () => buildStoryBeatSections(currentPage?.content),
    [currentPage?.content]
  );
  const totalLabs = course?.labs?.length ?? 0;
  const completedLabs = courseProgress?.completedLabs ?? 0;
  const labCompletionPercentage = totalLabs > 0 ? Math.min(100, Math.round((completedLabs / totalLabs) * 100)) : 0;
  const allLabsCompleted = (course?.labs || []).length === 0
    ? true
    : (course?.labs || []).every((lab) => labProgress[lab.id]?.completed);
  const remainingLabs = (course?.labs || []).filter((lab) => !labProgress[lab.id]?.completed).length;
  const isCourseCompleted = courseProgress?.completed ?? false;
  const currentPageLabs = course?.labs?.filter(lab => (lab as any).pageId === currentPage?.id) || [];
  const totalPages = courseContent?.pages?.length || 0;
  const storyBeatCount = storyBeatSections.length;
  const isStoryBeatMode = storyBeatCount > 1;
  const isLabSoftLocked = isStoryBeatMode && !showAllBeats && activeBeatIndex < storyBeatCount - 1;
  const activeBeatText = useMemo(() => {
    const activeSection = storyBeatSections[activeBeatIndex];
    return activeSection ? extractPlainText(activeSection.html) : '';
  }, [activeBeatIndex, storyBeatSections]);
  const filteredVoices = useMemo(() => {
    if (voiceGender === 'all') return ttsVoices;
    return ttsVoices.filter((voice) => voice.gender?.toLowerCase() === voiceGender);
  }, [ttsVoices, voiceGender]);

  useEffect(() => {
    if (!isStoryBeatMode) return;
    if (!pollyClient) {
      setTtsError("Amazon Polly is not configured");
      return;
    }
    let isMounted = true;
    setIsVoicesLoading(true);
    setTtsError(null);

    const loadVoices = async () => {
      try {
        const response = await pollyClient.send(
          new DescribeVoicesCommand({
            Engine: "neural",
            LanguageCode: "en-US",
          })
        );
        const voices: TtsVoice[] = (response.Voices ?? [])
          .map((voice) => ({
            id: voice.Id ?? "",
            name: voice.Name ?? voice.Id ?? "Unknown voice",
            gender: voice.Gender ?? null,
            previewUrl: null,
          }))
          .filter((voice) => voice.id)
          .filter((voice) => !['Joanna', 'Gregory'].includes(voice.id));
        if (!isMounted) return;
        setTtsVoices(voices);
      } catch (err) {
        if (!isMounted) return;
        setTtsError(err instanceof Error ? err.message : "Failed to load voices");
      } finally {
        if (!isMounted) return;
        setIsVoicesLoading(false);
      }
    };

    loadVoices();

    return () => {
      isMounted = false;
    };
  }, [isStoryBeatMode, pollyClient]);

  useEffect(() => {
    if (filteredVoices.length === 0) {
      setSelectedVoiceId('');
      return;
    }
    if (!filteredVoices.some((voice) => voice.id === selectedVoiceId)) {
      setSelectedVoiceId(filteredVoices[0].id);
    }
  }, [filteredVoices, selectedVoiceId]);

  const cleanupAudio = useCallback(() => {
    if (streamingReaderRef.current) {
      streamingReaderRef.current.cancel().catch(() => {});
      streamingReaderRef.current = null;
    }
    if (mediaSourceRef.current) {
      try {
        if (mediaSourceRef.current.readyState === "open") {
          mediaSourceRef.current.endOfStream();
        }
      } catch {
        // ignore
      }
      mediaSourceRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.oncanplay = null;
      audioRef.current.removeAttribute("src");
      audioRef.current.load();
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
  }, []);

  const stopNarration = useCallback(() => {
    ttsRequestRef.current += 1;
    cleanupAudio();
    setIsNarrating(false);
    setIsNarrationLoading(false);
  }, [cleanupAudio]);

  const playChunk = useCallback(async (text: string, requestId: number, chunkIndex: number, totalChunks: number): Promise<void> => {
    if (!pollyClient) throw new Error("Polly client not available");
    if (ttsRequestRef.current !== requestId) throw new Error("Request cancelled");

    console.log(`Playing chunk ${chunkIndex + 1}/${totalChunks}, length: ${text.length} chars`);

    try {
      const response = await pollyClient.send(new SynthesizeSpeechCommand({
        Text: text,
        OutputFormat: "mp3",
        VoiceId: selectedVoiceId as VoiceId,
        Engine: "neural",
      }));
      const audioStream = response.AudioStream;

      if (!audioStream) {
        throw new Error("Amazon Polly did not return audio");
      }

      if (ttsRequestRef.current !== requestId) throw new Error("Request cancelled");

      const audioBlob = await buildAudioBlob(audioStream);
      if (ttsRequestRef.current !== requestId) throw new Error("Request cancelled");
      
      const audioUrl = URL.createObjectURL(audioBlob);

      return new Promise<void>((resolve, reject) => {
        const audio = new Audio();
        
        const cleanup = () => {
          audio.onended = null;
          audio.onerror = null;
          URL.revokeObjectURL(audioUrl);
        };

        audio.onended = () => {
          console.log(`Chunk ${chunkIndex + 1}/${totalChunks} finished`);
          cleanup();
          resolve();
        };

        audio.onerror = (e) => {
          console.error(`Chunk ${chunkIndex + 1}/${totalChunks} error:`, e);
          cleanup();
          reject(new Error("Failed to play audio chunk"));
        };

        audio.src = audioUrl;
        audio.play().catch((err) => {
          cleanup();
          reject(err);
        });

        // Store reference for potential cleanup
        audioRef.current = audio;
        audioUrlRef.current = audioUrl;
      });
    } catch (err) {
      console.error(`Failed to load chunk ${chunkIndex + 1}/${totalChunks}:`, err);
      throw err;
    }
  }, [pollyClient, selectedVoiceId]);

  const playNarration = useCallback(async () => {
    if (!activeBeatText || !selectedVoiceId) return;
    if (!pollyClient) {
      setTtsError("Amazon Polly is not configured");
      return;
    }

    const requestId = ttsRequestRef.current + 1;
    ttsRequestRef.current = requestId;
    setIsNarrationLoading(true);
    setTtsError(null);
    cleanupAudio();

    try {
      const chunks = splitTextIntoChunks(activeBeatText);
      setTotalChunks(chunks.length);
      setCurrentChunkIndex(0);

      if (chunks.length > 1) {
        console.log(`Split narration into ${chunks.length} chunks:`, chunks.map(c => c.length));
      }

      setIsNarrating(true);
      setIsNarrationLoading(false);

      for (let i = 0; i < chunks.length; i++) {
        if (ttsRequestRef.current !== requestId) {
          console.log('Narration cancelled, stopping chunk playback');
          break;
        }
        
        setCurrentChunkIndex(i);
        console.log(`Starting chunk ${i + 1}/${chunks.length}`);
        
        await playChunk(chunks[i], requestId, i, chunks.length);
        
        console.log(`Completed chunk ${i + 1}/${chunks.length}`);
        
        if (ttsRequestRef.current !== requestId) {
          console.log('Narration cancelled after chunk completed');
          break;
        }
      }

      if (ttsRequestRef.current === requestId) {
        console.log('All chunks completed successfully');
        cleanupAudio();
        setIsNarrating(false);
        setCurrentChunkIndex(0);
        setTotalChunks(1);
      }
    } catch (err) {
      console.error('Narration error:', err);
      if (ttsRequestRef.current !== requestId) return;
      cleanupAudio();
      setIsNarrating(false);
      setCurrentChunkIndex(0);
      setTotalChunks(1);
      setTtsError(err instanceof Error ? err.message : "Failed to load narration");
    } finally {
      if (ttsRequestRef.current === requestId) {
        setIsNarrationLoading(false);
      }
    }
  }, [activeBeatText, cleanupAudio, playChunk, pollyClient, selectedVoiceId]);

  const handleNarrationToggle = useCallback(() => {
    if (isNarrating || isNarrationLoading) {
      stopNarration();
    } else {
      playNarration();
    }
  }, [isNarrating, isNarrationLoading, playNarration, stopNarration]);

  useEffect(() => {
    if (!isNarrating) return;
    playNarration();
  }, [activeBeatIndex, currentPageIndex, selectedVoiceId, isNarrating, playNarration]);

  useEffect(() => {
    if (!isStoryBeatMode) {
      stopNarration();
    }
  }, [isStoryBeatMode, stopNarration]);

  useEffect(() => {
    return () => {
      stopNarration();
    };
  }, [stopNarration]);
  
  useEffect(() => {
    const loadCourse = async () => {
      try {
        setLoading(true);
        setCourseProgress(null);
        const [courseDetail, progressList] = await Promise.all([
          apiService.getCourseById(id),
          apiService.getUserCoursesProgress().catch(() => [] as CourseWithProgressResponse[])
        ]);
        setCourseData(courseDetail);
        
        // Check for page parameter in URL
        if (typeof window !== "undefined") {
          const params = new URLSearchParams(window.location.search);
          const pageParam = params.get("page");
          if (pageParam) {
            const pageIndex = parseInt(pageParam, 10);
            const totalPages = courseDetail.course.content?.pages?.length || 0;
            setCurrentPageIndex(Math.min(Math.max(0, pageIndex), totalPages - 1));
          } else {
            // Reset to first page when course changes
            setCurrentPageIndex(0);
          }
        } else {
          setCurrentPageIndex(0);
        }

        const courseId = courseDetail.course.id;
        const courseSlug = courseDetail.course.slug;
        const matchedProgress = progressList.find((item) => {
          const itemId = (item as { id?: unknown, courseId?: unknown })?.id ?? (item as { courseId?: unknown })?.courseId;
          return String(itemId) === String(courseId) || (item as { slug?: string }).slug === courseSlug;
        }) || null;
setCourseProgress(matchedProgress);

        // Fetch lab progress
        const progressPromises = courseDetail.course.labs?.map(async (lab) => {
          try {
            const progress = await apiService.getLabProgress(lab.id);
            return { id: lab.id, progress };
          } catch {
            return { id: lab.id, progress: null };
          }
        }) || [];

        const progressResults = await Promise.all(progressPromises);
        const progressMap: Record<string, LabProgressResponse> = {};
        progressResults.forEach(({ id, progress }) => {
          if (progress) progressMap[id] = progress;
        });
        setLabProgress(progressMap);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load course');
      } finally {
        setLoading(false);
        // Scroll to breadcrumb section after course loads
        setTimeout(() => {
          // Find the breadcrumb div by its specific style and content pattern
          const breadcrumbDiv = Array.from(document.querySelectorAll('div')).find(el => 
            el.style.marginBottom === '32px' && 
            el.style.display === 'flex' &&
            el.innerHTML.includes('Home') && 
            el.innerHTML.includes('Courses')
          );
          if (breadcrumbDiv) {
            breadcrumbDiv.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else {
            // Fallback to course content if breadcrumb not found
            const courseContentElement = document.querySelector('.course-page-content');
            if (courseContentElement) {
              courseContentElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }
        }, 200);
      }
    };
    
    loadCourse();
  }, [id]);

  // Update currentPageIndex when URL page parameter changes
  useEffect(() => {
    if (courseData?.course && search.page !== undefined) {
      const pageIndex = typeof search.page === 'string' ? parseInt(search.page, 10) : search.page;
      const totalPages = courseData.course.content?.pages?.length || 0;
      setCurrentPageIndex(Math.min(Math.max(0, pageIndex), totalPages - 1));
    }
  }, [search.page, courseData]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const storedPreference = window.localStorage.getItem("courseStoryBeatShowAll");
    if (storedPreference === "true") {
      setShowAllBeats(true);
    }
  }, []);

  const refreshCourseProgress = useCallback(async () => {
    if (!courseData?.course) return;
    try {
      const progressList = await apiService.getUserCoursesProgress().catch(() => [] as CourseWithProgressResponse[]);
      const courseId = courseData.course.id;
      const courseSlug = courseData.course.slug;
      const matchedProgress = progressList.find((item) => {
        const itemId = (item as { id?: unknown, courseId?: unknown })?.id ?? (item as { courseId?: unknown })?.courseId;
        return String(itemId) === String(courseId) || (item as { slug?: string }).slug === courseSlug;
      }) || null;
      setCourseProgress(matchedProgress);
    } catch {
      console.warn('Failed to refresh course progress');
    }
  }, [courseData?.course]);

  const handleLabModalClose = useCallback(() => {
    setActiveLabId(null);
    refreshCourseProgress();
  }, [refreshCourseProgress]);
  
  // Listen for lab completion messages to refresh progress
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!event?.data || typeof event.data !== "object") return;
      if (event.data.type === "lab:completed") {
        // Refresh course progress when a lab is completed
        refreshCourseProgress();
        
        // Also refresh lab progress
        const refreshLabProgress = async () => {
          if (!courseData?.course) return;
          
          try {
            const progressPromises = courseData.course.labs?.map(async (lab) => {
              try {
                const progress = await apiService.getLabProgress(lab.id);
                return { id: lab.id, progress };
          } catch {
                return { id: lab.id, progress: null };
              }
            }) || [];

            const progressResults = await Promise.all(progressPromises);
            const progressMap: Record<string, LabProgressResponse> = {};
            progressResults.forEach(({ id, progress }) => {
              if (progress) progressMap[id] = progress;
            });
            setLabProgress(progressMap);
          } catch (err) {
            console.warn('Failed to refresh lab progress', err);
          }
        };
        
        refreshLabProgress();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [courseData, refreshCourseProgress]);

  const scrollToCourseContent = useCallback(() => {
    const courseContentElement = document.querySelector('.course-page-content');
    if (courseContentElement) {
      courseContentElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handlePageChange = useCallback((newPageIndex: number) => {
    setCurrentPageIndex(newPageIndex);
    // Scroll to course content after page change
    setTimeout(scrollToCourseContent, 100);
  }, [scrollToCourseContent]);

  const handleAdvanceBeat = useCallback((index: number) => {
    if (showAllBeats) return;
    const nextIndex = Math.min(index + 1, storyBeatSections.length - 1);
    setActiveBeatIndex(nextIndex);
    if (typeof window !== "undefined") {
      window.setTimeout(() => {
        beatSectionRefs.current[nextIndex]?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
    }
  }, [showAllBeats, storyBeatSections.length]);

  const handleToggleShowAll = useCallback(() => {
    const next = !showAllBeats;
    setShowAllBeats(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("courseStoryBeatShowAll", String(next));
    }
  }, [showAllBeats]);

  useEffect(() => {
    setActiveBeatIndex(0);
    beatSectionRefs.current = [];
  }, [currentPage?.id]);

  useEffect(() => {
    if (storyBeatSections.length > 0 && activeBeatIndex >= storyBeatSections.length) {
      setActiveBeatIndex(0);
    }
  }, [activeBeatIndex, storyBeatSections.length]);

  const renderStoryBeatHeader = () => (
    <div className="story-beat-header">
      <div className="story-beat-progress">
        <span>Content steps</span>
        <span>
          {showAllBeats
            ? "All visible"
            : `Step ${Math.min(activeBeatIndex + 1, storyBeatCount)} of ${storyBeatCount}`}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          className="story-beat-toggle"
          onClick={handleToggleShowAll}
        >
          {showAllBeats ? "Use content steps" : "Show all"}
        </button>
        <select
          value={voiceGender}
          onChange={(event) => setVoiceGender(event.target.value as 'all' | 'female' | 'male')}
          disabled={isVoicesLoading}
          style={{
            background: 'rgba(255, 255, 255, 0.08)',
            color: '#f5f5f5',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 6,
            padding: '6px 10px',
            fontSize: '0.85rem'
          }}
        >
          <option value="all">All voices</option>
          <option value="female">Female voices</option>
          <option value="male">Male voices</option>
        </select>
        <select
          value={selectedVoiceId}
          onChange={(event) => setSelectedVoiceId(event.target.value)}
          disabled={isVoicesLoading || filteredVoices.length === 0}
          style={{
            background: 'rgba(255, 255, 255, 0.08)',
            color: '#f5f5f5',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 6,
            padding: '6px 10px',
            fontSize: '0.85rem',
            minWidth: 160
          }}
        >
          {filteredVoices.length === 0 ? (
            <option value="">No voices available</option>
          ) : (
            filteredVoices.map((voice) => (
              <option key={voice.id} value={voice.id}>
                {voice.name}{voice.gender ? ` (${voice.gender})` : ''}
              </option>
            ))
          )}
        </select>
        <button
          type="button"
          className="story-beat-toggle"
          onClick={handleNarrationToggle}
          disabled={!activeBeatText || !selectedVoiceId || isNarrationLoading || isVoicesLoading}
        >
          {isNarrationLoading ? "Loading..." : isNarrating ? "Stop narrator" : "Play narrator"}
        </button>
        {isNarrating && totalChunks > 1 && (
          <div style={{
            fontSize: '0.75rem',
            color: '#94a3b8',
            marginTop: 4,
            opacity: 0.8
          }}>
            Part {currentChunkIndex + 1} of {totalChunks}
          </div>
        )}
      </div>
    </div>
  );

  const handleCompleteCourse = useCallback(async () => {
    try {
      await apiService.completeCourse(id);
      navigate({
        to: "/courses",
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          celebrate: "1",
        }),
      });
    } catch (error) {
      console.error('Failed to complete course:', error);
    }
  }, [navigate, id]);

  

  if (loading) {
    return (
      <ProtectedRoute>
        <div style={{ padding: 40, background: "#0a0f1a", color: "#f5f5f5", minHeight: "100vh" }}>
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: '1.2rem', opacity: 0.8 }}>Loading course...</div>
          </div>
        </div>
      </ProtectedRoute>
    );
  }
  
  if (error || !course) {
    return (
      <ProtectedRoute>
        <div style={{ padding: 40, background: "#0a0f1a", color: "#f5f5f5", minHeight: "100vh" }}>
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            color: '#ef4444',
            padding: 20,
            borderRadius: 8,
            marginBottom: 20
          }}>
            <h2>{error || 'Course not found'}</h2>
            <Link to="/courses" style={{ color: '#6b7280' }}>← Back to courses</Link>
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  const isPremiumContent = courseData?.course?.isPremium;
  const isLocked = isPremiumContent && user?.subscription !== 'premium';

  // Redirect to courses page if user tries to access premium content without subscription
  if (isLocked && courseData) {
    return (
      <ProtectedRoute>
        <div style={{ padding: 40, background: "#0a0f1a", color: "#f5f5f5", minHeight: "100vh" }}>
          <div style={{
            background: 'rgba(255, 165, 0, 0.1)',
            border: '1px solid rgba(255, 165, 0, 0.2)',
            color: '#ffa500',
            padding: 32,
            borderRadius: 12,
            margin: '40px auto',
            maxWidth: 600,
            textAlign: 'center'
          }}>
            <h2 style={{ fontSize: '1.8rem', marginBottom: 16 }}>🔒 Premium Course</h2>
            <p style={{ marginBottom: 24, fontSize: '1.1rem', opacity: 0.9 }}>
              This course requires a premium subscription to access.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link 
                to="/courses" 
                style={{
                  padding: '12px 24px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  color: '#f5f5f5',
                  textDecoration: 'none',
                  borderRadius: 8,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  fontSize: '1rem',
                  fontWeight: 500
                }}
              >
                ← Back to Courses
              </Link>
              <Link 
                to="/pricing" 
                style={{
                  padding: '12px 24px',
                  background: '#ffa500',
                  color: '#0a0f1a',
                  textDecoration: 'none',
                  borderRadius: 8,
                  fontSize: '1rem',
                  fontWeight: 500
                }}
              >
                Upgrade to Premium →
              </Link>
            </div>
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  return (
    <div
      style={{
        padding: 40,
        background: "#0a0f1a",
        color: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      {/* Breadcrumbs */}
      <div
        style={{
          marginBottom: 32,
          display: "flex",
          gap: 8,
          alignItems: "center",
          fontSize: "0.9rem",
          opacity: 0.8,
        }}
      >
        <Link to="/" style={{ color: "#6b7280", textDecoration: "none" }}>
          Home
        </Link>
        <span>{'>'}</span>
        <Link
          to="/courses"
          style={{ color: "#6b7280", textDecoration: "none" }}
        >
          Courses
        </Link>
        <span>{'>'}</span>
        <span style={{ color: "#4ade80" }}>{course.title}</span>
      </div>

      {/* Course Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: "2.5rem", marginBottom: 16, color: "#f5f5f5" }}>
          {course.title}
        </h1>
        <p
          style={{
            fontSize: "1.2rem",
            opacity: 0.9,
            lineHeight: 1.6,
            marginBottom: 24,
          }}
        >
          {course.description}
        </p>

        {/* Tags */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {course.tags?.map((t) => (
            <span
              key={t}
              style={{
                fontSize: 12,
                padding: "4px 12px",
                borderRadius: 999,
                background: "rgba(74, 222, 128, 0.1)",
                color: "#4ade80",
                border: "1px solid rgba(74, 222, 128, 0.2)",
              }}
            >
              {t}
            </span>
          ))}
          {isLocked && (
            <span
              style={{
                fontSize: 12,
                padding: "4px 12px",
                borderRadius: 999,
                background: "rgba(255, 165, 0, 0.1)",
                color: "#ffa500",
                border: "1px solid rgba(255, 165, 0, 0.2)",
              }}
            >
              🔒 Premium Content
            </span>
          )}
        </div>

        {totalLabs > 0 && (
          <div
            style={{
              marginTop: 24,
              padding: "16px 20px",
              borderRadius: 12,
              border: "1px solid rgba(34, 211, 238, 0.25)",
              background: "rgba(34, 211, 238, 0.08)",
              display: "flex",
              flexDirection: "column",
              gap: 8,
              maxWidth: 420,
            }}
          >
            <div style={{ fontSize: "0.95rem", opacity: 0.85 }}>
              Labs completed: <strong>{completedLabs}</strong> / {totalLabs}
            </div>
            <div
              style={{
                height: 6,
                borderRadius: 999,
                background: "rgba(255, 255, 255, 0.12)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${labCompletionPercentage}%`,
                  height: "100%",
                  transition: "width 0.3s ease",
                  background: labCompletionPercentage >= 100
                    ? "linear-gradient(90deg, #4ade80, #22d3ee)"
                    : "linear-gradient(90deg, #22d3ee, #38bdf8)",
                }}
              />
            </div>
            <div style={{ fontSize: "0.8rem", opacity: 0.7 }}>
              {labCompletionPercentage}% complete
            </div>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div style={{ marginBottom: 40 }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8
        }}>
          <span style={{ fontSize: '0.9rem', opacity: 0.8 }}>
            Page {currentPageIndex + 1} of {totalPages}
          </span>
          <span style={{ fontSize: '0.9rem', opacity: 0.8 }}>
            {Math.round(((currentPageIndex + 1) / totalPages) * 100)}% Complete
          </span>
        </div>
        <div style={{
          width: '100%',
          height: 6,
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: 3,
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${((currentPageIndex + 1) / totalPages) * 100}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #22d3ee, #4ade80)',
            transition: 'width 0.3s ease'
          }} />
        </div>
      </div>

      {/* Video Player - Shows if current page has a video */}
      {currentPage?.videoUrl && !settings.videoDisabled && (
        <StickyVideoPlayer
          key={currentPage.videoUrl}
          videoId={currentPage.videoUrl}
          courseTitle={course.title}
          disableSticky={settings.stickyMiniPlayerDisabled}
        />
      )}

      {/* Quick Page Navigation */}
      <div style={{ 
        maxWidth: "900px", 
        margin: "0 auto", 
        marginBottom: 24,
        paddingLeft: 0,
        paddingRight: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        flexWrap: "wrap"
      }}>
        <span style={{ fontSize: "0.9rem", opacity: 0.7, marginRight: 4 }}>Pages:</span>
        {pages.map((page, idx) => (
          <button
            key={page.id}
            type="button"
            onClick={() => {
              navigate({
                to: `/courses/${id}`,
                search: { page: idx }
              });
            }}
            style={{
              padding: "6px 12px",
              background: idx === currentPageIndex 
                ? "linear-gradient(135deg, #22d3ee, #4ade80)" 
                : "rgba(255, 255, 255, 0.1)",
              color: idx === currentPageIndex ? "#0a0f1a" : "#f5f5f5",
              border: idx === currentPageIndex 
                ? "none" 
                : "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: 6,
              fontSize: "0.85rem",
              fontWeight: idx === currentPageIndex ? 600 : 400,
              cursor: "pointer",
              transition: "all 0.2s ease",
              minWidth: 36
            }}
            onMouseEnter={(e) => {
              if (idx !== currentPageIndex) {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.15)";
              }
            }}
            onMouseLeave={(e) => {
              if (idx !== currentPageIndex) {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
              }
            }}
          >
            {idx + 1}
          </button>
        ))}
      </div>

      {/* Current Course Page */}
      <div style={{ maxWidth: "900px", margin: "0 auto", marginBottom: 60 }}>
        {currentPage && (
          <div className="course-page-content">
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 24
            }}>
              <div className="content-page-number">
                {currentPageIndex + 1}
              </div>
              <h2 style={{ 
                color: "#f5f5f5", 
                margin: 0,
                fontSize: '1.5rem'
              }}>
                {currentPage.title}
              </h2>
            </div>
            {ttsError && (
              <div style={{ marginBottom: 12, color: '#f87171', fontSize: '0.85rem' }}>
                {ttsError}
              </div>
            )}
            <div className="content-page-content">
              {isStoryBeatMode && showAllBeats && renderStoryBeatHeader()}
              {storyBeatSections.length > 0 ? (
                storyBeatSections.map((section, index) => {
                  const isUnlocked = !isStoryBeatMode || showAllBeats || index <= activeBeatIndex;
                  const isActiveBeat = isStoryBeatMode && !showAllBeats && index === activeBeatIndex;
                  const isMutedBeat = isStoryBeatMode && !showAllBeats && index !== activeBeatIndex;
                  const beatClassNames = [
                    "story-beat",
                    isUnlocked ? "story-beat--open" : "story-beat--locked",
                    isActiveBeat ? "story-beat--active" : null,
                    isMutedBeat ? "story-beat--muted" : null
                  ].filter(Boolean).join(" ");
                  return (
                    <div
                      key={section.id}
                      ref={(el) => {
                        beatSectionRefs.current[index] = el;
                      }}
                      className={beatClassNames}
                    >
                      {isStoryBeatMode && !showAllBeats && isActiveBeat && renderStoryBeatHeader()}
                      <div dangerouslySetInnerHTML={{ __html: section.html }} />
                      {isStoryBeatMode && !showAllBeats && isActiveBeat && index < storyBeatCount - 1 && (
                        <button
                          type="button"
                          className="story-beat-continue"
                          onClick={() => handleAdvanceBeat(index)}
                        >
                          Continue →
                        </button>
                      )}
                    </div>
                  );
                })
              ) : (
                <div dangerouslySetInnerHTML={{ __html: currentPage.content }} />
              )}
            </div>
          </div>
        )}

        {/* Labs for Current Page */}
        {currentPageLabs.length > 0 && (
          <div style={{ marginTop: 40 }} className="story-beat-labs">
            {isLabSoftLocked && (
              <div className="story-beat-labs-note">
                Finish the content steps above to bring labs into focus.
              </div>
            )}
            <div
              className={
                isLabSoftLocked
                  ? "story-beat-labs-content story-beat-labs-content--locked"
                  : "story-beat-labs-content"
              }
            >
              <h3 style={{ color: "#22d3ee", marginBottom: 20, fontSize: "1.3rem" }}>
                Practice Labs
              </h3>
              {currentPageLabs.map((lab) => (
              <div key={lab.id} id={`lab-${lab.id}`} style={{ marginBottom: 24, position: "relative" }}>
                {labProgress[lab.id]?.completed && (
                  <div className="course-lab-badge">Completed</div>
                )}
                <div className="lab-card" style={{ position: "relative" }}>
                  <div className="lab-card-inner" style={{ paddingTop: "24px" }}>
                    <div style={{ marginBottom: 16 }}>
                      <h3 style={{ color: "#22d3ee", marginBottom: 8 }}>
                        {lab.title}
                      </h3>
                      <p style={{ opacity: 0.8, fontSize: "0.9rem" }}>
                        {lab.level} • {lab.steps?.length || 0} steps
                      </p>
                      <p style={{ opacity: 0.7, fontSize: "0.85rem", marginTop: 8 }}>
                        {lab.description}
                      </p>
                    </div>
                    
                    {!isLocked ? (
                      <button
                        type="button"
                        onClick={() => {
                          setActiveLabId(lab.id);
                          // Store course and page info in sessionStorage for lab redirect
                          if (typeof window !== "undefined") {
                            sessionStorage.setItem('labRedirectInfo', JSON.stringify({
                              courseId: id,
                              pageIndex: currentPageIndex
                            }));
                          }
                        }}
                        style={{
                          padding: "8px 16px",
                          background: labProgress[lab.id]?.completed ? "#4ade80" : "#22d3ee",
                          color: "#0a0f1a",
                          textDecoration: "none",
                          borderRadius: 6,
                          fontSize: "0.9rem",
                          fontWeight: 500,
                          display: "inline-block",
                          cursor: "pointer",
                          border: "none"
                        }}
                      >
                        {labProgress[lab.id]?.completed ? "Do it again →" : "Start Lab →"}
                      </button>
                    ) : (
                      <span style={{
                        padding: "8px 16px",
                        background: "rgba(255, 165, 0, 0.1)",
                        color: "#ffa500",
                        borderRadius: 6,
                        fontSize: "0.9rem",
                        border: "1px solid rgba(255, 165, 0, 0.2)",
                        display: "inline-block"
                      }}>
                        🔒 Locked
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        )}

        {/* Navigation */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 60,
          paddingTop: 20,
          borderTop: "1px solid rgba(255, 255, 255, 0.1)"
        }}>
           {currentPageIndex > 0 ? (
            <button
              onClick={() => handlePageChange(currentPageIndex - 1)}
              style={{
                padding: "12px 24px",
                background: "rgba(255, 255, 255, 0.05)",
                color: "#f5f5f5",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: 8,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8
              }}
            >
              ← Previous
            </button>
           ) : (
            <div></div>
           )}

            {currentPageIndex < totalPages - 1 ? (
             <button
               onClick={() => handlePageChange(currentPageIndex + 1)}
               style={{
                 padding: "12px 24px",
                 background: "#22d3ee",
                 color: "#0a0f1a",
                 border: "none",
                 borderRadius: 8,
                 cursor: "pointer",
                 fontWeight: 500,
                 display: "inline-flex",
                 alignItems: "center",
                 gap: 8
               }}
             >
               Next →
             </button>
           ) : (
             isCourseCompleted ? (
               <div style={{
                 padding: "12px 24px",
                 background: "rgba(74, 222, 128, 0.2)",
                 color: "#4ade80",
                 border: "1px solid rgba(74, 222, 128, 0.3)",
                 borderRadius: 8,
                 fontWeight: 600,
                 display: "inline-flex",
                 alignItems: "center",
                 gap: 8,
                 fontSize: "1rem"
               }}>
                  Course completed!
               </div>
             ) : allLabsCompleted ? (
               <button
                 onClick={() => handleCompleteCourse()}
                 style={{
                   padding: "12px 24px",
                   background: "#4ade80",
                   color: "#0a0f1a",
                   border: "none",
                   borderRadius: 8,
                   fontWeight: 500,
                   display: "inline-flex",
                   alignItems: "center",
                   gap: 8,
                   cursor: "pointer"
                 }}
               >
                 Complete Course →
               </button>
             ) : (

              <div style={{ display: "grid", gap: 8, justifyItems: "end" }}>
                 <button
                   disabled
                   style={{
                     padding: "12px 24px",
                     background: "rgba(255,255,255,0.05)",
                     color: "rgba(245,245,245,0.5)",
                     border: "1px solid rgba(255,255,255,0.08)",
                     borderRadius: 8,
                     fontWeight: 500,
                     display: "inline-flex",
                     alignItems: "center",
                     gap: 8,
                     cursor: "not-allowed"
                   }}
                   title="Complete all labs to finish the course"
                 >
                   {isCourseCompleted ? "Course completed!" : "Complete Course →"}
                 </button>
                <span style={{ color: "#f97316", fontSize: "0.85rem" }}>
                  {remainingLabs === 1 ? "Finish the last lab to unlock completion" : `Finish the remaining ${remainingLabs} labs to unlock completion.`}
                </span>
              </div>
            )
          )}
        </div>
      </div>

      <div
        style={{
          textAlign: "center",
          borderTop: "1px solid #374151",
          paddingTop: 40,
          marginTop: 40,
        }}
      ></div>
      <Footer />
      {activeLabId && (
        <LabModal 
          labId={activeLabId} 
          onClose={handleLabModalClose} 
          isRetry={labProgress[activeLabId]?.completed || false}
        />
      )}
    </div>
  );
}
